import React, { useEffect } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, projectNameScheme, stringScheme } from 'src/utils/validation-schemes';
import {
  useActivateProjectMutation,
  useDeactivateProjectMutation,
  useGetProjectByIdQuery,
  useProjectDemoModeMutation,
  useUpdateProjectMutation,
} from 'src/services';
import { Button } from 'src/components/ui/button';
import { useTheme } from 'src/provider/ThemeProvider';
import { clsx } from 'clsx';
import { ChangeNameField } from 'src/pages/ControlDeskPage/ProjectControls/ProjectSettingsModal/ChangeNameField';
import { RateLimitsField } from 'src/pages/ControlDeskPage/ProjectControls/ProjectSettingsModal/RateLimitsField';
import { useGetUsersMeQuery } from 'src/services/usersService';
import { ROLES } from 'src/constants/roles';
import { ProductsTypeBack } from 'src/constants/products';
import { BaseFormToggleField } from 'src/components/BaseFormToggleField/BaseFormToggleField';
import { AutoCloseField } from 'src/pages/ControlDeskPage/ProjectControls/ProjectSettingsModal/AutoCloseField';
import moment from 'moment';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';

const aiPerSessionItems = [
  { label: 'minute', id: 1 },
  { label: 'hour', id: 2 },
  { label: 'day', id: 3 },
  { label: 'total', id: 4 },
];

const aiPerProjectItems = [
  { label: 'minute', id: 1 },
  { label: 'hour', id: 2 },
  { label: 'day', id: 3 },
  { label: 'week', id: 4 },
  { label: 'month', id: 5 },
];

export const ProjectSettingFields = ({ close = () => {}, onlyForm = false }) => {
  const notification = useNotification();
  const { id } = useParams();
  const { config } = useTheme();

  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;

  const { data: userInfo } = useGetUsersMeQuery();

  const isCosupportManager = userInfo?.role === ROLES.COSSUPORT_MANAGER;

  const [
    updateProject,
    {
      isLoading: isLoadingUpdateProject,
      isError: isUpdateProjectError,
      isSuccess: isUpdateProjectSuccess,
    },
  ] = useUpdateProjectMutation();

  const [
    updateDemoMode,
    { isLoading: isLoadingDemoMode, isError: isErrorUpdateDemoMode, isSuccess: isSuccessDemoMode },
  ] = useProjectDemoModeMutation();

  const [
    activateProject,
    {
      isLoading: isLoadingActivateProject,
      isError: isActivateProjectError,
      isSuccess: isSuccessActivateProject,
    },
  ] = useActivateProjectMutation();

  const [
    deactivateProject,
    {
      isLoading: deactivateProjectIsLoading,
      isError: isDeactivateProjectError,
      isSuccess: deactivateProjectIsSuccess,
    },
  ] = useDeactivateProjectMutation();

  const isActive = currentProject?.is_active;

  const isDemoMode = currentProject?.is_demo_mode;
  const duration = currentProject?.autoclose_time_window_minutes
    ? moment.duration(currentProject?.autoclose_time_window_minutes, 'minutes')
    : 0;

  const autocloseHours = duration ? Math.floor(duration.asHours()) : 0;
  const autocloseMinutes = duration ? duration.minutes() : 0;
  const projectNameDefault = currentProject?.name;
  const {
    handleSubmit,
    control,
    setValue,
    resetField,
    watch,
    trigger,
    formState: { isDirty },
  } = useForm<any>({
    defaultValues: {
      projectName: projectNameDefault,
      aiPerSessionToggle: false,
      aiPerSessionRadio: currentProject?.rate_limit_chat_per,
      aiPerSessionLimit: currentProject?.rate_limit_chat,
      aiPerProjectToggle: false,
      aiPerProjectRadio: currentProject?.rate_limit_project_per,
      aiPerProjectLimit: currentProject?.rate_limit_project,
      conversationsToggle: false,
      conversationsRadio: currentProject?.rate_limit_max_chats_per,
      conversationsLimit: currentProject?.rate_limit_max_chats,
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
      demoMode: isDemoMode,
      isActiveProject: isActive,
    },
    resolver: yupResolver(
      object({
        projectName: projectNameScheme({ required: true }).label('New name'),
        aiPerSessionRadio: stringScheme({ required: false }).label(''),
        aiPerSessionLimit: stringScheme({ required: false }).label('Limit'),
        aiPerSessionToggle: booleanScheme({ required: false }),
        conversationsRadio: stringScheme({ required: false }).label(''),
        conversationsLimit: stringScheme({ required: false }).label('Limit'),
        conversationsToggle: booleanScheme({ required: false }),
        aiPerProjectRadio: stringScheme({ required: false }).label(''),
        aiPerProjectLimit: stringScheme({ required: false }).label('Limit'),
        aiPerProjectToggle: booleanScheme({ required: false }),
        demoMode: booleanScheme({ required: false }),
        isActiveProject: booleanScheme({ required: false }),
      }),
    ),
  });

  const submit = (data) => {
    const isRateOrNameChanged =
      data.projectName !== currentProject?.name ||
      data.aiPerSessionRadio !== currentProject?.rate_limit_chat_per ||
      +data.aiPerSessionLimit !== currentProject?.rate_limit_chat ||
      data.aiPerProjectRadio !== currentProject?.rate_limit_project_per ||
      +data.aiPerProjectLimit !== currentProject?.rate_limit_project ||
      data.conversationsRadio !== currentProject?.rate_limit_max_chats_per ||
      +data.conversationsLimit !== currentProject?.rate_limit_max_chats;

    const autocloseMinutes = moment
      .duration(data.autocloseHours, 'hours')
      .add(data.autocloseMinutes, 'minutes')
      .asMinutes();

    const isAutocloseChanged = currentProject?.autoclose_time_window_minutes !== autocloseMinutes;

    if (isRateOrNameChanged || isAutocloseChanged) {
      updateProject({
        cocbi_project_id: currentProject.cocbi_project_id,
        use_cocbi_model: currentProject.use_cocbi_model,
        projectId: id,
        name: data.projectName.trim(),
        ai_reply_generation: currentProject.ai_reply_generation,
        ai_text_lense: currentProject.ai_text_lense,
        rate_limit_chat_per: data.aiPerSessionRadio,
        rate_limit_chat: Number(data.aiPerSessionLimit),
        rate_limit_project_per: data.aiPerProjectRadio,
        rate_limit_project: Number(data.aiPerProjectLimit),
        rate_limit_max_chats_per: data.conversationsRadio,
        rate_limit_max_chats: Number(data.conversationsLimit),
        autoclose_time_window_minutes: autocloseMinutes ? autocloseMinutes : null,
      }).then(() => {
        resetField('projectName', { defaultValue: data.projectName.trim() });
        resetField('aiPerSessionRadio', { defaultValue: data.aiPerSessionRadio });
        resetField('aiPerSessionLimit', { defaultValue: data.aiPerSessionLimit });
        resetField('aiPerProjectRadio', { defaultValue: data.aiPerProjectRadio });
        resetField('aiPerProjectLimit', { defaultValue: data.aiPerProjectLimit });
        resetField('conversationsRadio', { defaultValue: data.conversationsRadio });
        resetField('conversationsLimit', { defaultValue: data.conversationsLimit });
      });
    }

    if (data.demoMode !== isDemoMode) {
      updateDemoMode({
        id: id,
        project_type: ProductsTypeBack[currentProject?.project_type],
        enable: data.demoMode,
      }).then(() => resetField('demoMode', { defaultValue: data.demoMode }));
    }

    if (data.isActiveProject !== isActive) {
      if (data.isActiveProject) {
        activateProject({ projectId: id }).then(() =>
          resetField('isActiveProject', { defaultValue: data.isActiveProject }),
        );
      } else {
        deactivateProject({ projectId: id }).then(() =>
          resetField('isActiveProject', { defaultValue: data.isActiveProject }),
        );
      }
    }
  };

  useEffect(() => {
    if (isUpdateProjectError) {
      resetField('projectName', { defaultValue: projectNameDefault });
    }
  }, [isUpdateProjectError]);

  useEffect(() => {
    if (currentProject) {
      setValue('projectName', currentProject?.name);
    }
  }, [currentProject]);

  useEffect(() => {
    if (isSuccessActivateProject) {
      notification.success('Project has been activated!');
      close();
    }
  }, [isSuccessActivateProject]);

  useEffect(() => {
    if (deactivateProjectIsSuccess) {
      notification.success('Project has been deactivated!');
      close();
    }
  }, [deactivateProjectIsSuccess]);

  useEffect(() => {
    if (isUpdateProjectSuccess) {
      notification.success('Project has been updated!');
      close();
    }
  }, [isUpdateProjectSuccess]);

  useEffect(() => {
    if (isActivateProjectError) {
      resetField('isActiveProject', { defaultValue: isActive });
    }
  }, [isActivateProjectError]);

  useEffect(() => {
    if (isDeactivateProjectError) {
      resetField('isActiveProject', { defaultValue: isActive });
    }
  }, [isDeactivateProjectError]);

  useEffect(() => {
    if (isSuccessDemoMode) {
      notification.success('Demo mode was updated!');
      close();
    }
  }, [isSuccessDemoMode]);

  useEffect(() => {
    if (isErrorUpdateDemoMode) {
      resetField('demoMode', { defaultValue: isDemoMode });
    }
  }, [isErrorUpdateDemoMode]);

  const isLoading =
    isLoadingUpdateProject ||
    isLoadingDemoMode ||
    isLoadingActivateProject ||
    deactivateProjectIsLoading;

  const isAutocloseEnabled = watch('autocloseEnabled');

  useEffect(() => {
    resetField('autocloseHours', {
      defaultValue:
        isAutocloseEnabled && currentProject?.autoclose_time_window_minutes ? autocloseHours : 0,
    });
    resetField('autocloseMinutes', {
      defaultValue:
        isAutocloseEnabled && currentProject?.autoclose_time_window_minutes ? autocloseMinutes : 0,
    });
  }, [isAutocloseEnabled]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="flex flex-col gap-5">
        <ChangeNameField control={control} />
        <div className="flex flex-col gap-5">
          <p className={clsx(config?.textColorClassName, 'font-bold text-sm mb-1')}>Rate limits</p>
          <div className="flex flex-col">
            <RateLimitsField
              control={control}
              label={'Max AI responses per session'}
              radioItemsName={'aiPerSessionRadio'}
              toggleName={'aiPerSessionToggle'}
              limitName={'aiPerSessionLimit'}
              radioItems={aiPerSessionItems}
            />
            <div className="h-px bg-light w-full my-5" />
            <RateLimitsField
              control={control}
              label={'Max AI responses per project'}
              radioItemsName={'aiPerProjectRadio'}
              toggleName={'aiPerProjectToggle'}
              limitName={'aiPerProjectLimit'}
              radioItems={aiPerProjectItems}
            />
            <div className="h-px bg-light w-full my-5" />
            <RateLimitsField
              control={control}
              label={'Max conversations per project'}
              radioItemsName={'conversationsRadio'}
              toggleName={'conversationsToggle'}
              limitName={'conversationsLimit'}
              radioItems={aiPerProjectItems}
            />
            <div className="h-px bg-light w-full mt-5" />
          </div>
          <div className={'flex flex-col gap-5'}>
            <p className={clsx(config.textColorClassName, 'font-bold text-sm mb-1')}>
              Conversations
            </p>
            <AutoCloseField
              control={control}
              label={
                <div className={'flex flex-row items-center gap-2.5'}>
                  Auto-close on inactivity{' '}
                  <InputInfoToolTip
                    contentClassName={'max-w-[350px]'}
                    infoToolTip={{
                      description:
                        'Automatically mark conversations as resolved when customers have not responded within the set time.',
                      side: 'right',
                    }}
                  />
                </div>
              }
              toggleName={'autocloseEnabled'}
              limitHourName={'autocloseHours'}
              limitMinuteName={'autocloseMinutes'}
              disabled={!isAutocloseEnabled}
            />
            <div className="h-px bg-light w-full" />
          </div>
        </div>
        {isCosupportManager && (
          <div className="flex flex-col gap-5">
            <p className={clsx(config.textColorClassName, 'font-bold text-sm mb-1')}>
              Settings for CoSupport Managers
            </p>
            <div className="flex flex-col">
              <BaseFormToggleField control={control} label="Demo mode" name="demoMode" />
              <div className="h-px bg-light w-full my-5" />
              <BaseFormToggleField
                control={control}
                name="isActiveProject"
                label="Project activated"
              />
            </div>
          </div>
        )}
      </div>
      <div className="sticky bottom-0">
        <div className="flex w-full items-center justify-center gap-2.5 pb-6 pt-5 bg-white">
          {!onlyForm && (
            <Button
              type="button"
              variant="outline"
              className="transparent black min-w-28"
              onClick={() => close()}
            >
              Cancel
            </Button>
          )}
          <Button disabled={!isDirty || isLoading} type="submit" className="min-w-28">
            {isLoading ? 'Loading...' : 'Save'}
          </Button>
        </div>
      </div>
    </form>
  );
};
